import { generateKeyBetween } from "@/lib/contract";

interface Positioned {
  position: string;
  id: string;
}

/**
 * The one ordering for anything with a fractional position: cards, columns,
 * projects.
 *
 * A plain code-unit comparison, and never `localeCompare`. Positions are
 * fractional-index keys whose order is defined character by character; a
 * locale-aware comparison sorts "a0V" and "a0v" by human alphabet rules and
 * scrambles a column in a way that only shows up for some users' locales.
 *
 * Ties are broken by id. Two people appending to the same column at the same
 * instant can both compute the same key, and without a tie-break their two
 * cards would swap places between renders.
 */
export function byPosition(a: Positioned, b: Positioned): number {
  if (a.position < b.position) return -1;
  if (a.position > b.position) return 1;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
}

/**
 * The position for an item dropped at `index` in `sorted`, a list already in
 * order that does not include the item being placed.
 */
export function positionAt(sorted: readonly Positioned[], index: number): string {
  const before = index > 0 ? (sorted[index - 1]?.position ?? null) : null;
  let after = sorted[index]?.position ?? null;

  // Tied neighbours (the concurrent-append case above) leave no room between
  // them, so look past the tie for the next strictly greater key. The item
  // lands just after the tied pair, which is as close as an ordering can get.
  if (before !== null && after !== null && after <= before) {
    after = null;
    for (const item of sorted.slice(index)) {
      if (item.position > before) {
        after = item.position;
        break;
      }
    }
  }

  return generateKeyBetween(before, after);
}

export function appendPosition(sorted: readonly Positioned[]): string {
  return positionAt(sorted, sorted.length);
}
